import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Brand, BrandId, Car, CarId, DatabaseShape, Model, ModelId, Supplier, SupplierId, AdditionalCharge, Fund, FundTransaction } from './types';
import { useAuth } from './contexts/AuthContext';
import { initialDb } from './seed';
import { readDatabase as readLocalStorage, writeDatabase as writeLocalStorage } from './storage';
import * as firestoreService from './services/firestoreService';
import { testFirestoreConnection } from './utils/firestoreTest';

interface StoreContextValue extends DatabaseShape {
  loading: boolean;
  error: string | null;
  usingFirestore: boolean;
  isReconnecting: boolean;
  addBrand: (name: string, group?: string) => Promise<Brand>;
  addModel: (brandId: BrandId, modelName: string) => Promise<Model>;
  addSupplier: (name: string, contact?: string, phone?: string, email?: string, address?: string) => Promise<Supplier>;
  updateSupplier: (supplierId: SupplierId, updates: Partial<Supplier>) => Promise<void>;
  deleteSupplier: (supplierId: SupplierId) => Promise<void>;
  addCar: (brandId: BrandId, modelId: ModelId, carData?: Partial<Car>) => Promise<Car>;
  updateCar: (carId: CarId, updates: Partial<Car>) => Promise<void>;
  deleteCar: (carId: CarId) => Promise<void>;
  rentCar: (carId: CarId, startDate: Date, endDate: Date, customerName?: string, customerContact?: string, customerEmail?: string, customerLicense?: string, paymentType?: 'advance' | 'after') => Promise<boolean>;
  returnCar: (carId: CarId, newMileage: number, additionalCharges?: AdditionalCharge[]) => Promise<boolean>;
  extendRental: (carId: CarId, newEndDate: Date, newEndTime: string) => Promise<boolean>;
  deleteRentalHistory: (carId: CarId, rentalId: string) => Promise<boolean>;
  deleteSaleHistory: (carId: CarId) => Promise<boolean>;
  addFundTransaction: (transaction: Omit<FundTransaction, 'id' | 'timestamp'>) => Promise<void>;
  deleteFundTransaction: (transactionId: string) => Promise<void>;
  updateFundManually: (newBalance: number, newTotalBenefit: number, newTotalCharges: number) => Promise<void>;
  markCarAsAvailable: (carId: CarId) => Promise<void>;
  retryConnection: () => Promise<void>;
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined);

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Migration function to add isPaid field to existing cars
const migrateDatabase = (db: DatabaseShape): DatabaseShape => {
  const migratedCars = db.cars.map(car => ({
    ...car,
    isPaid: car.isPaid !== undefined ? car.isPaid : false // Default to false for existing cars
  }));
  
  return {
    ...db,
    cars: migratedCars
  };
};

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [db, setDb] = useState<DatabaseShape>({ brands: [], models: [], suppliers: [], cars: [], fund: undefined });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFirestore, setUsingFirestore] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [pendingOperations, setPendingOperations] = useState<Array<() => Promise<void>>>([]);

  // Initialize database - try Firestore first, fallback to localStorage
  useEffect(() => {
    // Check if current user is demo user
    const isDemoUser = currentUser?.uid === 'demo-user' || currentUser?.email === 'demo@carrental.com';
    
    if (!currentUser || isDemoUser) {
      // If not authenticated or demo user, use localStorage only
      const localData = migrateDatabase(readLocalStorage());
      setDb(localData);
      setLoading(false);
      setUsingFirestore(false);
      if (isDemoUser) {
        console.log('🎯 Demo user detected - using local storage only');
      }
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const initializeDatabase = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to connect to Firestore with timeout
        
        // Test Firestore connection first with timeout
        const connectionTest = await Promise.race([
          testFirestoreConnection(),
          new Promise<boolean>((_, reject) => 
            setTimeout(() => reject(new Error('Connection timeout after 15 seconds')), 15000)
          )
        ]);
        
        if (!connectionTest) {
          throw new Error('Firestore connection test failed');
        }
        
        // Test Firestore connection with a simple read
        const testData = await Promise.race([
          firestoreService.readDatabase(),
          new Promise<any>((_, reject) => 
            setTimeout(() => reject(new Error('Database read timeout after 10 seconds')), 10000)
          )
        ]);
        
        
        // Initialize with seed data if empty
        if (testData.brands.length === 0 && testData.models.length === 0 && testData.cars.length === 0) {
          await firestoreService.initializeDatabase(migrateDatabase(initialDb));
        }

        // Set up real-time listener
        unsubscribe = firestoreService.subscribeToDatabase((data) => {
          setDb(migrateDatabase(data));
          setLoading(false);
          setUsingFirestore(true);
        });

      } catch (err) {
        console.warn('❌ Firestore connection failed, falling back to localStorage:', err);
        
        // Provide specific error messages
        let errorMessage = 'Using offline mode - data will be stored locally';
        if (err instanceof Error) {
          if (err.message.includes('timeout')) {
            errorMessage = 'Firestore connection timeout - using offline mode';
            console.warn('⏱️ This might be due to network issues or Firestore configuration problems');
          } else if (err.message.includes('permission')) {
            errorMessage = 'Firestore permission denied - using offline mode';
            console.warn('🔒 Check your Firestore security rules');
          } else if (err.message.includes('not found')) {
            errorMessage = 'Firestore database not found - using offline mode';
            console.warn('🗄️ Make sure Firestore is properly configured in Firebase Console');
          }
        }
        
        // Fallback to localStorage
        try {
          const localData = migrateDatabase(readLocalStorage());
          setDb(localData);
        } catch (localErr) {
          console.error('❌ localStorage also failed:', localErr);
          setDb(migrateDatabase(initialDb));
          errorMessage = 'Using default data - both Firestore and localStorage failed';
        }
        
        setLoading(false);
        setUsingFirestore(false);
        setError(errorMessage);
      }
    };

    initializeDatabase();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Helper function to persist data based on current mode
  const persistData = useCallback(async (newDb: DatabaseShape) => {
    setDb(newDb);
    
    if (usingFirestore && currentUser) {
      // Don't need to manually persist - Firestore handles this
      return;
    } else {
      // Persist to localStorage
      writeLocalStorage(newDb);
    }
  }, [usingFirestore, currentUser]);

  const addBrand = useCallback(async (name: string, group?: string): Promise<Brand> => {
    const brand: Brand = { 
      id: generateId('brand'), 
      name: name.trim(), 
      group 
    };
    
    if (usingFirestore) {
      await firestoreService.addBrand(brand);
    } else {
      const newDb = { ...db, brands: [...db.brands, brand] };
      await persistData(newDb);
    }
    
    return brand;
  }, [db, usingFirestore, persistData]);

  const addModel = useCallback(async (brandId: BrandId, modelName: string): Promise<Model> => {
    const model: Model = { 
      id: generateId('model'), 
      name: modelName.trim(), 
      brandId 
    };
    
    if (usingFirestore) {
      await firestoreService.addModel(model);
    } else {
      const newDb = { ...db, models: [...db.models, model] };
      await persistData(newDb);
    }
    
    return model;
  }, [db, usingFirestore, persistData]);


  const updateCar = useCallback(async (carId: CarId, updates: Partial<Car>): Promise<void> => {
    if (usingFirestore) {
      await firestoreService.updateCar(carId, updates);
    } else {
      const newDb = {
        ...db,
        cars: db.cars.map(car => 
          car.id === carId ? { ...car, ...updates } : car
        )
      };
      await persistData(newDb);
    }
  }, [db, usingFirestore, persistData]);

  const deleteCar = useCallback(async (carId: CarId): Promise<void> => {
    if (usingFirestore) {
      await firestoreService.deleteCar(carId);
    } else {
      const newDb = {
        ...db,
        cars: db.cars.filter(car => car.id !== carId)
      };
      await persistData(newDb);
    }
  }, [db, usingFirestore, persistData]);

  const extendRental = useCallback(async (carId: CarId, newEndDate: Date, newEndTime: string): Promise<boolean> => {
    const car = db.cars.find(c => c.id === carId);
    if (!car || !car.isRented) return false;

    const [hours, minutes] = newEndTime.split(':').map(Number);
    const extendedDate = new Date(newEndDate);
    extendedDate.setHours(hours, minutes, 0, 0);

    await updateCar(carId, { rentedUntil: extendedDate });
    return true;
  }, [db.cars, updateCar]);

  const deleteSaleHistory = useCallback(async (carId: CarId): Promise<boolean> => {
    const car = db.cars.find(c => c.id === carId);
    if (!car || !car.isSold) return false;

    const updates: Partial<Car> = {
      isSold: false,
      soldDate: undefined,
      soldToCustomer: undefined,
      soldToCustomerPhone: undefined,
      soldToCustomerLicense: undefined,
      finalSalePrice: undefined,
    };

    await updateCar(carId, updates);
    return true;
  }, [db.cars, updateCar]);

  const addFundTransaction = useCallback(async (transactionData: Omit<FundTransaction, 'id' | 'timestamp'>): Promise<void> => {
    // Remove undefined values to prevent Firestore errors
    const cleanedData: any = {
      type: transactionData.type,
      amount: transactionData.amount,
      description: transactionData.description,
      category: transactionData.category
    };
    
    // Only add supplierId if it's defined
    if (transactionData.supplierId) {
      cleanedData.supplierId = transactionData.supplierId;
    }
    
    const transaction: FundTransaction = {
      id: generateId('txn'),
      timestamp: new Date(),
      ...cleanedData
    };

    const currentFund = db.fund || {
      id: 'fund-1',
      currentBalance: 0,
      transactions: [],
      lastUpdated: new Date()
    };

    const balanceChange = transactionData.type === 'add_money' ? transactionData.amount : -transactionData.amount;
    const newBalance = currentFund.currentBalance + balanceChange;

    // Handle special benefit categories that also update Total Benefit
    let updatedManualBenefit = currentFund.manualTotalBenefit;
    if (transactionData.category === 'benefit_rental' || transactionData.category === 'benefit_sale') {
      // Calculate current benefit (manual or calculated)
      const soldCars = db.cars.filter(car => car.isSold && car.finalSalePrice && car.purchasePrice);
      const totalSalesProfit = soldCars.reduce((sum, car) => {
        const profit = (car.finalSalePrice || 0) - (car.purchasePrice || 0);
        return sum + profit;
      }, 0);
      const rentalTransactions = currentFund.transactions.filter(t => t.category === 'rental' && t.type === 'add_money');
      const totalRentalRevenue = rentalTransactions.reduce((sum, t) => sum + t.amount, 0);
      const calculatedBenefit = totalSalesProfit + totalRentalRevenue;
      
      const currentBenefit = currentFund.manualTotalBenefit ?? calculatedBenefit;
      updatedManualBenefit = currentBenefit + transactionData.amount;
    }

    // Handle special "décaissement" category that doesn't update Total Charges
    let updatedManualCharges = currentFund.manualTotalCharges;
    if (transactionData.type === 'add_charge' && transactionData.category === 'decaissement') {
      // For décaissement, we need to set manualTotalCharges to prevent it from being counted
      // Calculate current charges (manual or calculated)
      const chargeTransactions = currentFund.transactions.filter(t => 
        t.type === 'add_charge' && t.category !== 'decaissement'
      );
      const calculatedCharges = chargeTransactions.reduce((sum, t) => sum + t.amount, 0);
      
      // Keep charges the same (don't add this transaction to charges)
      updatedManualCharges = currentFund.manualTotalCharges ?? calculatedCharges;
    }

    const updatedFund: Fund = {
      ...currentFund,
      currentBalance: newBalance,
      transactions: [...currentFund.transactions, transaction],
      lastUpdated: new Date(),
      ...(updatedManualBenefit !== undefined && { manualTotalBenefit: updatedManualBenefit }),
      ...(updatedManualCharges !== undefined && { manualTotalCharges: updatedManualCharges })
    };

    // Update supplier balance if this is a supplier transaction
    let updatedSuppliers = db.suppliers;
    if (transactionData.supplierId && transactionData.category === 'supplier') {
      updatedSuppliers = db.suppliers.map(supplier => {
        if (supplier.id === transactionData.supplierId) {
          const currentBalance = supplier.balance || 0;
          // For supplier balance (negative = we owe them):
          // Car Purchase (description starts with "Car Purchase"): SUBTRACTS (increases debt: 0 - 8,000,000 = -8,000,000)
          // Supplier Payment (add_charge from Fund page): ADDS (reduces debt: -11,550,000 + 1,550,000 = -10,000,000)
          const isCarPurchase = transactionData.description?.startsWith('Car Purchase');
          const supplierBalanceChange = isCarPurchase ? -transactionData.amount : transactionData.amount;
          return {
            ...supplier,
            balance: currentBalance + supplierBalanceChange
          };
        }
        return supplier;
      });
    }

    // Update local state immediately
    setDb(prev => ({ 
      ...prev, 
      fund: updatedFund,
      suppliers: updatedSuppliers
    }));

    // Sync with storage
    if (usingFirestore) {
      try {
        await firestoreService.updateFund(updatedFund);
        // Update supplier if balance changed
        if (transactionData.supplierId) {
          const updatedSupplier = updatedSuppliers.find(s => s.id === transactionData.supplierId);
          if (updatedSupplier) {
            await updateSupplier(updatedSupplier.id, updatedSupplier);
          }
        }
      } catch (error) {
        console.error('Failed to update fund in Firestore:', error);
        // Revert local change if Firestore update fails
        setDb(prev => ({ ...prev, fund: currentFund, suppliers: db.suppliers }));
        throw error;
      }
    } else {
      // Store locally
      const updatedDb = { ...db, fund: updatedFund, suppliers: updatedSuppliers };
      writeLocalStorage(updatedDb);
    }
  }, [db, usingFirestore]);

  const rentCar = useCallback(async (carId: CarId, startDate: Date, endDate: Date, customerName?: string, customerContact?: string, customerEmail?: string, customerLicense?: string, paymentType?: 'advance' | 'after'): Promise<boolean> => {
    const car = db.cars.find(c => c.id === carId);
    if (!car || car.isRented || car.isSold || !car.isPaid) return false;

    const updates: Partial<Car> = {
      isRented: true,
      rentedFrom: startDate,
      rentedUntil: endDate,
      rentalStartMileage: car.mileageKm || 0,
      currentCustomerName: customerName,
      currentCustomerContact: customerContact,
      currentCustomerEmail: customerEmail,
      currentCustomerLicense: customerLicense,
      rentalPaymentType: paymentType || 'after', // Default to after
    };

    await updateCar(carId, updates);

    // If payment in advance, add money to fund immediately
    if (paymentType === 'advance' && car.dailyRate) {
      const timeDiff = endDate.getTime() - startDate.getTime();
      const totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) || 1;
      const totalAmount = car.dailyRate * totalDays;

      // Get brand and model names for better description
      const brand = db.brands.find(b => b.id === car.brandId);
      const model = db.models.find(m => m.id === car.modelId);
      const carName = `${brand?.name || 'Unknown'} ${model?.name || 'Unknown'}`;

      await addFundTransaction({
        type: 'add_money',
        amount: totalAmount,
        description: `Rental Payment (Advance) - ${carName} (${totalDays} days) - ${customerName || 'Unknown Customer'}`,
        category: 'rental'
      });
    }

    return true;
  }, [db.cars, db.brands, db.models, updateCar, addFundTransaction]);

  const addCar = useCallback(async (brandId: BrandId, modelId: ModelId, carData?: Partial<Car>): Promise<Car> => {
    const car: Car = {
      id: generateId('car'),
      brandId,
      modelId,
      rating: 0,
      inGarage: true,
      isPaid: false,
      isRented: false,
      isForSale: false,
      isSold: false,
      ...carData,
    };

    // If car has supplier and purchase price, create supplier debt
    if (car.supplierId && car.purchasePrice) {
      // Create fund transaction for the car purchase (this will handle supplier balance update)
      const brand = db.brands.find(b => b.id === brandId);
      const model = db.models.find(m => m.id === modelId);
      const supplier = db.suppliers.find(s => s.id === car.supplierId);
      const carName = `${brand?.name || 'Unknown'} ${model?.name || 'Unknown'}`;
      
      await addFundTransaction({
        type: 'add_charge',
        amount: car.purchasePrice,
        description: `Car Purchase - ${carName} from ${supplier?.name || 'Unknown Supplier'}`,
        category: 'supplier',
        supplierId: car.supplierId
      });
    }
    
    if (usingFirestore) {
      await firestoreService.addCar(car);
    } else {
      const newDb = { ...db, cars: [...db.cars, car] };
      await persistData(newDb);
    }
    
    return car;
  }, [db, usingFirestore, persistData, addFundTransaction]);

  const deleteFundTransaction = useCallback(async (transactionId: string): Promise<void> => {
    const currentFund = db.fund;
    if (!currentFund) {
      throw new Error('No fund data found');
    }

    const transactionToDelete = currentFund.transactions.find(t => t.id === transactionId);
    if (!transactionToDelete) {
      throw new Error('Transaction not found');
    }

    // Remove the transaction from the list
    const updatedTransactions = currentFund.transactions.filter(t => t.id !== transactionId);
    
    // Recalculate balance by removing the transaction's effect
    const balanceChange = transactionToDelete.type === 'add_money' ? -transactionToDelete.amount : transactionToDelete.amount;
    const newBalance = currentFund.currentBalance + balanceChange;

    // Handle reverting special benefit categories
    let updatedManualBenefit = currentFund.manualTotalBenefit;
    if (transactionToDelete.category === 'benefit_rental' || transactionToDelete.category === 'benefit_sale') {
      // If there's a manual benefit set, subtract the deleted transaction amount
      if (currentFund.manualTotalBenefit !== undefined) {
        updatedManualBenefit = currentFund.manualTotalBenefit - transactionToDelete.amount;
      }
    }

    // Handle reverting décaissement category (restore charges)
    let updatedManualCharges = currentFund.manualTotalCharges;
    if (transactionToDelete.category === 'decaissement') {
      // If there's a manual charges set, we need to recalculate it
      if (currentFund.manualTotalCharges !== undefined) {
        // Calculate charges from remaining transactions (excluding décaissement)
        const chargeTransactions = updatedTransactions.filter(t => 
          t.type === 'add_charge' && t.category !== 'decaissement'
        );
        updatedManualCharges = chargeTransactions.reduce((sum, t) => sum + t.amount, 0);
      }
    }

    const updatedFund: Fund = {
      ...currentFund,
      currentBalance: newBalance,
      transactions: updatedTransactions,
      lastUpdated: new Date(),
      ...(updatedManualBenefit !== undefined && { manualTotalBenefit: updatedManualBenefit }),
      ...(updatedManualCharges !== undefined && { manualTotalCharges: updatedManualCharges })
    };

    // Reverse supplier balance if this was a supplier transaction
    let updatedSuppliers = db.suppliers;
    if (transactionToDelete.supplierId && transactionToDelete.category === 'supplier') {
      updatedSuppliers = db.suppliers.map(supplier => {
        if (supplier.id === transactionToDelete.supplierId) {
          const currentBalance = supplier.balance || 0;
          // Reverse the original transaction effect
          const isCarPurchase = transactionToDelete.description?.startsWith('Car Purchase');
          const originalChange = isCarPurchase ? -transactionToDelete.amount : transactionToDelete.amount;
          const balanceChange = -originalChange; // Reverse it
          return {
            ...supplier,
            balance: currentBalance + balanceChange
          };
        }
        return supplier;
      });
    }

    // Update local state immediately
    setDb(prev => ({ 
      ...prev, 
      fund: updatedFund,
      suppliers: updatedSuppliers
    }));

    // Sync with storage
    if (usingFirestore) {
      try {
        await firestoreService.updateFund(updatedFund);
        // Update supplier if balance changed
        if (transactionToDelete.supplierId) {
          const updatedSupplier = updatedSuppliers.find(s => s.id === transactionToDelete.supplierId);
          if (updatedSupplier) {
            await updateSupplier(updatedSupplier.id, updatedSupplier);
          }
        }
      } catch (error) {
        console.error('Failed to delete transaction in Firestore:', error);
        // Revert local change if Firestore update fails
        setDb(prev => ({ ...prev, fund: currentFund, suppliers: db.suppliers }));
        throw error;
      }
    } else {
      // Store locally
      const updatedDb = { ...db, fund: updatedFund, suppliers: updatedSuppliers };
      writeLocalStorage(updatedDb);
    }
  }, [db, usingFirestore]);

  const deleteRentalHistory = useCallback(async (carId: CarId, rentalId: string): Promise<boolean> => {
    const car = db.cars.find(c => c.id === carId);
    if (!car || !car.rentalHistory) return false;

    // Find the rental record to get the amount
    const rentalRecord = car.rentalHistory.find(record => record.id === rentalId);
    if (!rentalRecord) return false;

    const updatedHistory = car.rentalHistory.filter(record => record.id !== rentalId);
    await updateCar(carId, { rentalHistory: updatedHistory });

    // Remove rental revenue from fund by adding a charge
    if (rentalRecord.totalAmount > 0) {
      // Get brand and model names for better description
      const brand = db.brands.find(b => b.id === car.brandId);
      const model = db.models.find(m => m.id === car.modelId);
      const carName = `${brand?.name || 'Unknown'} ${model?.name || 'Unknown'}`;
      
      await addFundTransaction({
        type: 'add_charge',
        amount: rentalRecord.totalAmount,
        description: `Rental History Deleted - ${carName} (${rentalRecord.totalDays} days)`,
        category: 'rental'
      });
    }

    return true;
  }, [db.cars, db.brands, db.models, updateCar, addFundTransaction]);

  const returnCar = useCallback(async (carId: CarId, newMileage: number, additionalCharges: AdditionalCharge[] = []): Promise<boolean> => {
    const car = db.cars.find(c => c.id === carId);
    if (!car || !car.isRented) return false;

    // Fix invalid dates that cause NaN
    const startDate = car.rentedFrom ? new Date(car.rentedFrom) : new Date();
    const endDate = new Date();
    
    // Validate dates
    if (isNaN(startDate.getTime())) {
      console.warn('Invalid start date for car:', carId, 'using current date');
      startDate.setTime(endDate.getTime());
    }
    
    const timeDiff = endDate.getTime() - startDate.getTime();
    
    let totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    if (totalDays <= 0 || isNaN(totalDays)) {
      totalDays = 1;
    }

    const baseAmount = (car.dailyRate || 0) * totalDays;
    const additionalAmount = additionalCharges.reduce((sum, charge) => sum + charge.amount, 0);
    const totalAmount = baseAmount + additionalAmount;

    const rentalRecord = {
      id: generateId('rental'),
      startDate: car.rentedFrom!,
      endDate,
      startMileage: car.rentalStartMileage || 0,
      endMileage: newMileage,
      totalDays,
      totalAmount,
      additionalCharges,
      customerName: car.currentCustomerName,
      customerContact: car.currentCustomerContact,
      customerEmail: car.currentCustomerEmail,
      customerLicense: car.currentCustomerLicense,
    };

    const existingHistory = car.rentalHistory || [];
    const updates: Partial<Car> = {
      isRented: false,
      rentedFrom: undefined,
      rentedUntil: undefined,
      rentalStartMileage: undefined,
      currentCustomerName: undefined,
      currentCustomerContact: undefined,
      currentCustomerEmail: undefined,
      currentCustomerLicense: undefined,
      rentalPaymentType: undefined,
      mileageKm: newMileage,
      rentalHistory: [...existingHistory, rentalRecord],
    };

    await updateCar(carId, updates);

    // Add rental revenue to fund
    // If payment was in advance, only add additional charges
    // If payment was after, add full amount (base + charges)
    const paymentType = car.rentalPaymentType || 'after';
    const amountToAdd = paymentType === 'advance' ? additionalAmount : totalAmount;
    
    if (amountToAdd > 0) {
      // Get brand and model names for better description
      const brand = db.brands.find(b => b.id === car.brandId);
      const model = db.models.find(m => m.id === car.modelId);
      const carName = `${brand?.name || 'Unknown'} ${model?.name || 'Unknown'}`;
      
      const description = paymentType === 'advance' 
        ? `Rental Additional Charges - ${carName}` 
        : `Rental Revenue - ${carName} (${totalDays} days)`;
      
      await addFundTransaction({
        type: 'add_money',
        amount: amountToAdd,
        description,
        category: 'rental'
      });
    }

    return true;
  }, [db.cars, db.brands, db.models, updateCar, addFundTransaction]);

  const markCarAsAvailable = useCallback(async (carId: CarId): Promise<void> => {
    const car = db.cars.find(c => c.id === carId);
    if (!car || !car.isSold) {
      throw new Error('Car not found or not sold');
    }

    const updates: Partial<Car> = {
      isSold: false,
      soldDate: undefined,
      soldToCustomer: undefined,
      soldToCustomerPhone: undefined,
      soldToCustomerLicense: undefined,
      finalSalePrice: undefined,
      inGarage: true, // Put the car back in garage
      isForSale: false // Remove from sale
    };

    await updateCar(carId, updates);
  }, [db.cars, updateCar]);

  // Retry connection function
  const retryConnection = useCallback(async () => {
    if (!currentUser || usingFirestore || isReconnecting) return;
    
    setIsReconnecting(true);
    
    try {
      // Test connection
      const connectionTest = await Promise.race([
        testFirestoreConnection(),
        new Promise<boolean>((_, reject) => 
          setTimeout(() => reject(new Error('Connection timeout after 10 seconds')), 10000)
        )
      ]);
      
      if (connectionTest) {
        
        // Sync local data to Firestore
        const localData = migrateDatabase(db);
        await firestoreService.initializeDatabase(localData);
        
        // Set up real-time listener
        firestoreService.subscribeToDatabase((data) => {
          setDb(migrateDatabase(data));
          setUsingFirestore(true);
          setError(null);
        });
        
        // Execute any pending operations
        for (const operation of pendingOperations) {
          try {
            await operation();
          } catch (err) {
            console.error('Failed to execute pending operation:', err);
          }
        }
        setPendingOperations([]);
        
      } else {
        throw new Error('Connection test failed');
      }
    } catch (err) {
      console.warn('❌ Reconnection failed:', err);
    } finally {
      setIsReconnecting(false);
    }
  }, [currentUser, usingFirestore, isReconnecting, db, pendingOperations]);

  // Auto-retry connection every 30 seconds when offline
  useEffect(() => {
    if (!usingFirestore && currentUser && !isReconnecting) {
      const interval = setInterval(() => {
        retryConnection();
      }, 30000); // Try every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [usingFirestore, currentUser, isReconnecting, retryConnection]);

  // Supplier management functions
  const addSupplier = useCallback(async (name: string, contact?: string, phone?: string, email?: string, address?: string): Promise<Supplier> => {
    const supplier: Supplier = { 
      id: generateId('supplier'), 
      name: name.trim(),
      contact: contact?.trim(),
      phone: phone?.trim(),
      email: email?.trim(),
      address: address?.trim()
    };
    
    if (usingFirestore) {
      await firestoreService.addSupplier(supplier);
    } else {
      const newDb = { ...db, suppliers: [...db.suppliers, supplier] };
      setDb(newDb);
      writeLocalStorage(newDb);
    }
    
    return supplier;
  }, [db, usingFirestore]);

  const updateSupplier = useCallback(async (supplierId: SupplierId, updates: Partial<Supplier>): Promise<void> => {
    if (usingFirestore) {
      await firestoreService.updateSupplier(supplierId, updates);
    } else {
      const newDb = {
        ...db,
        suppliers: db.suppliers.map(supplier => 
          supplier.id === supplierId ? { ...supplier, ...updates } : supplier
        )
      };
      setDb(newDb);
      writeLocalStorage(newDb);
    }
  }, [db, usingFirestore]);

  const deleteSupplier = useCallback(async (supplierId: SupplierId): Promise<void> => {
    if (usingFirestore) {
      await firestoreService.deleteSupplier(supplierId);
    } else {
      const newDb = {
        ...db,
        suppliers: db.suppliers.filter(supplier => supplier.id !== supplierId)
      };
      setDb(newDb);
      writeLocalStorage(newDb);
    }
  }, [db, usingFirestore]);

  const updateFundManually = useCallback(async (newBalance: number, newTotalBenefit: number, newTotalCharges: number): Promise<void> => {
    const currentFund = db.fund || {
      id: 'fund-1',
      currentBalance: 0,
      transactions: [],
      lastUpdated: new Date()
    };

    // Direct update without creating transaction history
    const updatedFund: Fund = {
      ...currentFund,
      currentBalance: newBalance,
      manualTotalBenefit: newTotalBenefit,
      manualTotalCharges: newTotalCharges,
      lastUpdated: new Date()
    };

    // Update local state immediately
    setDb(prev => ({ 
      ...prev, 
      fund: updatedFund
    }));

    // Sync with storage
    if (usingFirestore) {
      try {
        await firestoreService.updateFund(updatedFund);
      } catch (error) {
        console.error('Failed to update fund in Firestore:', error);
        // Revert local change if Firestore update fails
        setDb(prev => ({ ...prev, fund: currentFund }));
        throw error;
      }
    } else {
      // Store locally
      const updatedDb = { ...db, fund: updatedFund };
      writeLocalStorage(updatedDb);
    }
  }, [db, usingFirestore]);

  const value: StoreContextValue = {
    ...db,
    loading,
    error,
    usingFirestore,
    isReconnecting,
    addBrand,
    addModel,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    addCar,
    updateCar,
    deleteCar,
    rentCar,
    returnCar,
    extendRental,
    deleteRentalHistory,
    deleteSaleHistory,
    addFundTransaction,
    deleteFundTransaction,
    updateFundManually,
    markCarAsAvailable,
    retryConnection,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export function useStore(): StoreContextValue {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
}

// Helper hooks
export function useBrandById(brandId: BrandId): Brand | undefined {
  const { brands } = useStore();
  return brands.find(brand => brand.id === brandId);
}

export function useModelById(modelId: ModelId): Model | undefined {
  const { models } = useStore();
  return models.find(model => model.id === modelId);
}

export function useCarById(carId: CarId): Car | undefined {
  const { cars } = useStore();
  return cars.find(car => car.id === carId);
}

export function useSupplierById(supplierId: SupplierId): Supplier | undefined {
  const { suppliers } = useStore();
  return suppliers?.find(supplier => supplier.id === supplierId);
}
